import React, { useState } from "react";
import { Button, Modal, Spin, message, Card, Typography, Row, Col } from "antd";
import {
  PrinterOutlined,
  BookOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { load_cc_manual, HEADERS_BASIC90 } from "../api/apiCent90";
import DATA_TYPE_OPTIONS from "../data/dataTypeOptions";
import AddBarcodeLetter from "../preview/AddBarcodeLetter";

dayjs.extend(utc);
dayjs.extend(timezone);

// กล่องแสดงข้อมูลสัญญา 1 ช่อง (ไอคอน + หัวข้อ + ค่า)
const InfoTile = ({ icon, label, value, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      height: "100%",
      padding: "10px 16px",
      borderRadius: 10,
      background: `${color}0f`,
      border: `1px solid ${color}33`,
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        color,
        background: `${color}1f`,
      }}
    >
      {icon}
    </div>
    <div style={{ textAlign: "left", minWidth: 0 }}>
      <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#262626" }}>
        {value}
      </div>
    </div>
  </div>
);

const { Title } = Typography;

/**
 * ModalEMSLetter
 * ปุ่ม "พิมพ์จดหมาย" (อยู่ท้ายแถวแต่ละรอบในประวัติจดหมาย) เปิด Modal แล้วโหลดสัญญาของรอบนั้นทันที
 * แสดงบัญชี / รอบวันที่ / เลขสัญญาแบบอ่านอย่างเดียว แล้วพิมพ์จดหมาย / ยิง EMS ผ่าน AddBarcodeLetter
 *
 * load-cc-h ส่ง flagStatus: 2 เพื่อเห็นเฉพาะงานธุรการ (แยกจากเร่งรัด flag_status=1)
 * API ไม่มี param contno -> ดึงทั้งรอบแล้วกรองเลขสัญญาฝั่งหน้าเว็บ
 *
 * props:
 *  - contno         : เลขสัญญา
 *  - contractSchema : บัญชี
 *  - roundDate      : รอบวันที่
 *  - size           : ขนาดปุ่ม (default "large")
 *  - onSaved        : เรียกหลังบันทึก EMS สำเร็จ (ให้ parent โหลดประวัติใหม่)
 */
function ModalEMSLetter({
  contno,
  contractSchema,
  roundDate,
  size = "large",
  onSaved,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataMainEMS, setDataMainEMS] = useState([]);
  const [loading, setLoading] = useState(false);

  // รูปแบบวันที่ที่ load-cc-h / AddBarcodeLetter ใช้
  const dateEMS = roundDate
    ? dayjs(roundDate).tz("Asia/Bangkok").format("YYYY-MM-DDT00:00:00+07:00")
    : "";
  const schemaLabel =
    DATA_TYPE_OPTIONS.find((o) => o.value === contractSchema)?.label ||
    contractSchema ||
    "-";

  //เปิด Modal แล้วโหลดข้อมูลทันที
  const handleOpen = () => {
    setIsModalOpen(true);
    loadDataEMS();
  };

  //ปิด Modal + ล้างข้อมูล
  const handleClose = () => {
    setDataMainEMS([]);
    setIsModalOpen(false);
  };

  const loadDataEMS = async () => {
    if (!contno || !dateEMS) return;
    try {
      setLoading(true);
      setDataMainEMS([]); // ล้างก่อน ให้ AddBarcodeLetter รีเซ็ตข้อมูลพิมพ์ของสัญญาเดิม
      const res = await axios.post(
         load_cc_manual ,
        {
          date: dateEMS,
          // contractSchema,
          // company: "3",
          // flagStatus: 2,
          contno:contno,
        },
        { headers: HEADERS_BASIC90 },
      );
      const dataB = res.data.mainData || [];
      const found = dataB.filter(
        (item) => String(item.contractNo).trim() === String(contno).trim(),
      );
      setDataMainEMS(found);
      if (found.length === 0)
        message.warning("ไม่พบเลขสัญญานี้ในรอบวันที่ที่เลือก");
    } catch (err) {
      console.log(err);
      message.error("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        size={size}
        icon={<PrinterOutlined />}
        onClick={handleOpen}
      >
        พิมพ์จดหมาย
      </Button>

      <Modal
        title="พิมพ์จดหมาย"
        open={isModalOpen}
        onCancel={handleClose}
        width={"90%"}
        footer={null}
        maskClosable={false}
        destroyOnClose
      >
        <div
          style={{
            width: "100%",
            maxWidth: 1300,
            margin: "0 auto",
            boxSizing: "border-box",
            overflowX: "hidden",
          }}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              marginBottom: 2,
              width: "100%",
            }}
            bodyStyle={{ padding: "8px 20px 12px" }}
          >
            <Title
              level={5}
              style={{ marginTop: 0, marginBottom: 8, textAlign: "center" }}
            >
              ค้นหาสัญญาสำหรับพิมพ์จดหมายออกมือ
            </Title>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <InfoTile
                  icon={<BookOutlined />}
                  label="บัญชี"
                  value={schemaLabel}
                  color="#1677ff"
                />
              </Col>
              <Col xs={24} sm={8}>
                <InfoTile
                  icon={<CalendarOutlined />}
                  label="รอบวันที่"
                  value={
                    roundDate ? dayjs(roundDate).format("DD/MM/YYYY") : "-"
                  }
                  color="#fa8c16"
                />
              </Col>
              <Col xs={24} sm={8}>
                <InfoTile
                  icon={<FileTextOutlined />}
                  label="เลขสัญญา"
                  value={contno || "-"}
                  color="#52c41a"
                />
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                width: "100%",
              }}
              bodyStyle={{ padding: "12px 16px", overflowX: "auto" }}
            >
              <AddBarcodeLetter
                data={dataMainEMS}
                date={dateEMS}
                onBack={handleClose}
                refreshCalendar={() => {
                  loadDataEMS();
                  if (onSaved) onSaved();
                }}
                roundDate={dateEMS}
              />
            </Card>
          </Spin>
        </div>
      </Modal>
    </>
  );
}

export default ModalEMSLetter;
